// src/features/profile/slices/profileSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  createUpdateProfile, 
  uploadProfilePicture, 
  getBookmarkedBlogs, 
  getUserDiscussionsAndComments, 
  getUserUpvotedBlogs, 
  getUserCuratedLists,
  getUserDiscussionsPaginated,
  getUserCommentsPaginated,
  getUserUpvotedBlogsPaginated,
  getUserCuratedListsPaginated
} from '../actions/profileActions'; 
import { getAllUserInfoByEmail } from '../../auth/actions/authActions'; 
import { deleteBookmark, saveBlogBookmark } from '../actions/profileActions';
import { convertStringArrayToTagOptionsArray } from '../../../utils/conversionUtil';
import { logout } from '../../auth/slices/authSlice';
import { 
  ProfileState, 
  ProfilePaginatedState, 
  ProfileDiscussion,
  ProfileComment,
  ProfileUpvotedBlog,
  ProfileCuratedList,
  ProfileDataCategory
} from '../types/profile.types';

// bookmarkedBlogs holds two shapes: rows from GET /api/blogs/bookmarks/ (where
// `id` is the BlogBookmark pk and the blog's id is `blog_id`) and blog objects
// pushed after a save (where `id` is the blog id). Always compare on the blog id.
export const getBookmarkedBlogId = (bookmark: any): number =>
  bookmark.blog_id ?? bookmark.id;

const initialState: ProfileState = {
  profilePicture: '',
  bio: '',
  interests: [],
  hasProfile: false,
  bookmarkedBlogs: [],
  bookmarksVersion: 0,
  userDiscussions: [],
  userComments: [],
  upvotedBlogs: [],
  userCuratedLists: [],
  
  // New paginated states
  paginatedDiscussions: {
    items: [],
    pagination: null,
    isLoading: false,
    error: null,
  },
  paginatedComments: {
    items: [],
    pagination: null,
    isLoading: false,
    error: null,
  },
  paginatedUpvotedBlogs: {
    items: [],
    pagination: null,
    isLoading: false,
    error: null,
  },
  paginatedCuratedLists: {
    items: [],
    pagination: null,
    isLoading: false,
    error: null,
  },
  
  isLoading: false,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: () => initialState,
    // Add reducer to clear paginated data
    clearPaginatedData: (state, action: PayloadAction<ProfileDataCategory>) => {
      switch (action.payload) {
        case 'discussions':
          state.paginatedDiscussions = {
            items: [],
            pagination: null,
            isLoading: false,
            error: null,
          };
          break;
        case 'comments':
          state.paginatedComments = {
            items: [],
            pagination: null,
            isLoading: false,
            error: null,
          };
          break;
        case 'upvotedBlogs':
          state.paginatedUpvotedBlogs = {
            items: [],
            pagination: null,
            isLoading: false,
            error: null,
          };
          break;
        case 'curatedLists':
          state.paginatedCuratedLists = {
            items: [],
            pagination: null,
            isLoading: false,
            error: null,
          };
          break;
      }
    },
  },
  extraReducers: (builder) => {
    // 1. Listen to Auth Action
    builder.addCase(getAllUserInfoByEmail.fulfilled, (state, action) => {
      const profileRes = action.payload.profile;
      if (profileRes) {
        state.bio = profileRes.about_me || '';
        state.interests = profileRes.tags ? convertStringArrayToTagOptionsArray(profileRes.tags) : [];
        state.profilePicture = profileRes.avatar || '';
        state.hasProfile = !!(profileRes.about_me);
      }
    });

    // 2. Profile Updates
    builder.addCase(uploadProfilePicture.fulfilled, (state, action) => {
      state.profilePicture = action.payload['avatar-url'];
    });
    
    builder.addCase(createUpdateProfile.fulfilled, (state, action) => {
      state.profilePicture = action.payload.avatar;
      state.bio = action.payload.about_me;
      state.interests = convertStringArrayToTagOptionsArray(action.payload.tags);
      state.hasProfile = true;
    });

    // 3. Activity & Bookmarks (legacy)
    builder.addCase(getBookmarkedBlogs.fulfilled, (state, action) => {
      // A save/delete happened while this request was in flight, so the
      // response may predate it; keep the local list instead of reverting it.
      if (action.payload.startVersion !== (state.bookmarksVersion ?? 0)) return;
      state.bookmarkedBlogs = action.payload['bookmarked_blogs'];
    });

    // Bump on both pending and fulfilled so a refetch that starts mid-request
    // is also dropped.
    const bumpBookmarksVersion = (state: ProfileState) => {
      state.bookmarksVersion = (state.bookmarksVersion ?? 0) + 1;
    };
    builder.addCase(saveBlogBookmark.pending, bumpBookmarksVersion);

    builder.addCase(saveBlogBookmark.fulfilled, (state, action) => {
      bumpBookmarksVersion(state);
      const blog = action.payload.blog;
      if (!state.bookmarkedBlogs.some(b => getBookmarkedBlogId(b) === blog.id)) {
        state.bookmarkedBlogs.push(blog);
      }
    });

    builder.addCase(deleteBookmark.pending, (state, action) => {
      bumpBookmarksVersion(state);
      const blogId = action.meta.arg.blogId;
      state.bookmarkedBlogs = state.bookmarkedBlogs.filter(
        b => getBookmarkedBlogId(b) !== blogId
      );
    });
    // Not on rejected: the thunk's failure path starts a resync refetch that
    // must be allowed to land.
    builder.addCase(deleteBookmark.fulfilled, bumpBookmarksVersion);

    builder.addCase(getUserDiscussionsAndComments.fulfilled, (state, action) => {
      state.userDiscussions = action.payload.discussions || [];
      state.userComments = action.payload.comments || [];
    });

    builder.addCase(getUserUpvotedBlogs.fulfilled, (state, action) => {
      state.upvotedBlogs = action.payload.upvoted_blogs || [];
    });

    builder.addCase(getUserCuratedLists.fulfilled, (state, action) => {
      state.userCuratedLists = action.payload || [];
      state.isLoading = false;
      state.error = null;
    });
    
    builder.addCase(getUserCuratedLists.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string || 'Failed to load curated lists';
      state.userCuratedLists = [];
    });

    // 4. Paginated Actions
    // Discussions
    builder.addCase(getUserDiscussionsPaginated.pending, (state) => {
      state.paginatedDiscussions.isLoading = true;
      state.paginatedDiscussions.error = null;
    });
    builder.addCase(getUserDiscussionsPaginated.fulfilled, (state, action) => {
      state.paginatedDiscussions.items = action.payload.items;
      state.paginatedDiscussions.pagination = action.payload.pagination;
      state.paginatedDiscussions.isLoading = false;
    });
    builder.addCase(getUserDiscussionsPaginated.rejected, (state, action) => {
      state.paginatedDiscussions.isLoading = false;
      state.paginatedDiscussions.error = action.payload as string;
    });

    // Comments
    builder.addCase(getUserCommentsPaginated.pending, (state) => {
      state.paginatedComments.isLoading = true;
      state.paginatedComments.error = null;
    });
    builder.addCase(getUserCommentsPaginated.fulfilled, (state, action) => {
      state.paginatedComments.items = action.payload.items;
      state.paginatedComments.pagination = action.payload.pagination;
      state.paginatedComments.isLoading = false;
    });
    builder.addCase(getUserCommentsPaginated.rejected, (state, action) => {
      state.paginatedComments.isLoading = false;
      state.paginatedComments.error = action.payload as string;
    });

    // Upvoted Blogs
    builder.addCase(getUserUpvotedBlogsPaginated.pending, (state) => {
      state.paginatedUpvotedBlogs.isLoading = true;
      state.paginatedUpvotedBlogs.error = null;
    });
    builder.addCase(getUserUpvotedBlogsPaginated.fulfilled, (state, action) => {
      state.paginatedUpvotedBlogs.items = action.payload.items;
      state.paginatedUpvotedBlogs.pagination = action.payload.pagination;
      state.paginatedUpvotedBlogs.isLoading = false;
    });
    builder.addCase(getUserUpvotedBlogsPaginated.rejected, (state, action) => {
      state.paginatedUpvotedBlogs.isLoading = false;
      state.paginatedUpvotedBlogs.error = action.payload as string;
    });

    // Curated Lists
    builder.addCase(getUserCuratedListsPaginated.pending, (state) => {
      state.paginatedCuratedLists.isLoading = true;
      state.paginatedCuratedLists.error = null;
    });
    builder.addCase(getUserCuratedListsPaginated.fulfilled, (state, action) => {
      state.paginatedCuratedLists.items = action.payload.items;
      state.paginatedCuratedLists.pagination = action.payload.pagination;
      state.paginatedCuratedLists.isLoading = false;
    });
    builder.addCase(getUserCuratedListsPaginated.rejected, (state, action) => {
      state.paginatedCuratedLists.isLoading = false;
      state.paginatedCuratedLists.error = action.payload as string;
    });

    builder.addCase(logout, () => {
      return initialState;
    });
  },
});

export const { clearProfile, clearPaginatedData } = profileSlice.actions;
export default profileSlice.reducer;